import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Sparkles,
  MapPin,
  Users,
  Activity,
  Radio,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Clock,
  Gauge,
  TrendingUp,
  ChevronDown,
  X,
  LayoutDashboard,
  Star,
  BarChart3
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import useManagedCrous from '../../hooks/useManagedCrous';
import useAllCrousLive from '../../hooks/useAllCrousLive';
import ReviewsTab from './components/ReviewsTab';
import StatsTab from './components/StatsTab';
import './dashboard.css';

/* ── helpers ──────────────────────────────────────────────────────────── */

function levelLabel(v) {
  if (v === null || v === undefined) return 'Aucune donnée';
  if (v < 0.7) return 'Vide';
  if (v < 1.5) return 'Moyen';
  return 'Blindé';
}

function levelClass(v) {
  if (v === null || v === undefined) return 'silent';
  if (v < 0.7) return 'low';
  if (v < 1.5) return 'mid';
  return 'high';
}

function colorFor(level) {
  if (level === null || level === undefined) return '#94A3B8';
  if (level < 0.7) return '#10B981';
  if (level < 1.5) return '#F59E0B';
  return '#EF4444';
}

function formatAgo(ts) {
  if (!ts) return '—';
  const delta = Date.now() - ts;
  if (delta < 60_000) return "à l'instant";
  if (delta < 3_600_000) return `il y a ${Math.round(delta / 60_000)} min`;
  const h = Math.round(delta / 3_600_000);
  return `il y a ${h}h`;
}

function serviceWindowLabel() {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  const t = h * 60 + m;
  if (t >= 690 && t < 810) {
    const left = 810 - t;
    return { label: 'Service midi', sub: `fin dans ${left} min`, active: true };
  }
  if (t >= 1080 && t < 1200) {
    const left = 1200 - t;
    return { label: 'Service soir', sub: `fin dans ${left} min`, active: true };
  }
  if (t < 690) {
    const left = 690 - t;
    const hh = Math.floor(left / 60);
    const mm = left % 60;
    return { label: 'Hors service', sub: `midi dans ${hh}h${mm > 0 ? mm.toString().padStart(2, '0') : ''}`, active: false };
  }
  if (t >= 810 && t < 1080) {
    const left = 1080 - t;
    const hh = Math.floor(left / 60);
    const mm = left % 60;
    return { label: 'Hors service', sub: `soir dans ${hh}h${mm > 0 ? mm.toString().padStart(2, '0') : ''}`, active: false };
  }
  return { label: 'Hors service', sub: '', active: false };
}

/* ── KPI bar ─────────────────────────────────────────────────────────── */

function KpiBar({ stats, service }) {
  return (
    <div className="db-kpi-bar">
      <div className={`db-service-chip ${service.active ? 'is-active' : ''}`}>
        <Clock size={14} />
        <strong>{service.label}</strong>
        {service.sub && <span>{service.sub}</span>}
      </div>
      <div className="db-kpis">
        <div className="db-kpi">
          <span>RU suivis</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="db-kpi db-kpi-high">
          <span>Blindés</span>
          <strong>{stats.full}</strong>
        </div>
        <div className="db-kpi db-kpi-mid">
          <span>Moyens</span>
          <strong>{stats.moderate}</strong>
        </div>
        <div className="db-kpi db-kpi-low">
          <span>Fluides</span>
          <strong>{stats.calm}</strong>
        </div>
        <div className="db-kpi db-kpi-silent">
          <span>Silencieux</span>
          <strong>{stats.silent}</strong>
        </div>
        <div className="db-kpi">
          <span>Pings 15 min</span>
          <strong>{stats.totalPings}</strong>
        </div>
      </div>
    </div>
  );
}

/* ── Trend chip ──────────────────────────────────────────────────────── */

function TrendChip({ trend }) {
  if (trend === 'up') return <span className="db-trend db-trend-up"><ArrowUpRight size={13} /></span>;
  if (trend === 'down') return <span className="db-trend db-trend-down"><ArrowDownRight size={13} /></span>;
  if (trend === 'new') return <span className="db-trend db-trend-new"><Sparkles size={13} /></span>;
  return <span className="db-trend db-trend-flat"><Minus size={13} /></span>;
}

/* ── Mini gauge SVG ──────────────────────────────────────────────────── */

function MiniGauge({ level, size = 40 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const ratio = level === null || level === undefined ? 0 : Math.min(1, level / 2);
  const color = colorFor(level);
  return (
    <svg width={size} height={size} className="db-mini-gauge">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - ratio)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize="11" fontWeight="900">
        {level === null || level === undefined ? '—' : level.toFixed(1)}
      </text>
    </svg>
  );
}

/* ── RU table row ────────────────────────────────────────────────────── */

function RuRow({ crous, pulse, onSelect, isSelected }) {
  const p = pulse || { level: null, count: 0, lastTs: null, trend: 'flat' };
  const lc = levelClass(p.level);
  const estimatedCrowd = p.level !== null && crous.capacity
    ? Math.round((p.level / 2) * crous.capacity * 0.8)
    : null;

  const overrideActive = crous.adminOverride && crous.adminOverride.active === true &&
    (!crous.adminOverride.expiresAt || crous.adminOverride.expiresAt > Date.now());

  return (
    <button
      type="button"
      className={`db-ru-row ${isSelected ? 'is-selected' : ''}`}
      onClick={() => onSelect(crous.id)}
    >
      <div className="db-ru-status">
        <MiniGauge level={p.level} size={38} />
      </div>

      <div className="db-ru-info">
        <div className="db-ru-name">
          <strong>{crous.name || crous.id}</strong>
          {overrideActive && <span className="db-override-badge">Override</span>}
        </div>
        <div className="db-ru-meta">
          {crous.campus && <span><MapPin size={11} /> {crous.campus}</span>}
          {crous.capacity && <span><Users size={11} /> {crous.capacity}</span>}
        </div>
      </div>

      <div className="db-ru-level">
        <span className={`db-level-label db-level-${lc}`}>{levelLabel(p.level)}</span>
      </div>

      <div className="db-ru-trend">
        <TrendChip trend={p.trend} />
      </div>

      <div className="db-ru-pings">
        <strong>{p.count}</strong>
        <span>pings</span>
      </div>

      <div className="db-ru-crowd">
        <strong>{estimatedCrowd !== null ? estimatedCrowd : '—'}</strong>
        <span>pers.</span>
      </div>

      <div className="db-ru-last">
        <span>{formatAgo(p.lastTs)}</span>
      </div>

      <div className="db-ru-arrow">
        <ArrowRight size={14} />
      </div>
    </button>
  );
}

/* ── Detail drawer ───────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { value: 'empty', label: 'Vide' },
  { value: 'moderate', label: 'Moyen' },
  { value: 'full', label: 'Blindé' },
  { value: 'closed', label: 'Fermé' }
];

function DetailDrawer({ crous, pulse, onClose }) {
  const p = pulse || { level: null, count: 0, lastTs: null, trend: 'flat' };
  const color = colorFor(p.level);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const overrideActive = crous.adminOverride && crous.adminOverride.active === true &&
    (!crous.adminOverride.expiresAt || crous.adminOverride.expiresAt > Date.now());

  const estimatedCrowd = p.level !== null && crous.capacity
    ? Math.round((p.level / 2) * crous.capacity * 0.8)
    : null;

  const flash = (msg, kind) => {
    setFeedback({ msg, kind });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 3000);
  };

  const applyOverride = async (payload) => {
    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'setCrousOverride');
      await fn({ crousId: crous.id, ...payload });
      flash(payload.clear ? 'Override levé.' : `Override "${payload.status}" appliqué.`, 'ok');
    } catch (err) {
      flash(err.message || "Échec.", 'err');
    } finally {
      setSubmitting(false);
    }
  };

  if (!crous) return null;

  return (
    <div className="db-drawer">
      <div className="db-drawer-head">
        <div>
          <div className="db-drawer-kicker">Restaurant universitaire</div>
          <h3>{crous.name || crous.id}</h3>
          <div className="db-drawer-meta">
            {crous.campus && <span><MapPin size={12} /> {crous.campus}</span>}
            {crous.capacity && <span><Users size={12} /> Cap. {crous.capacity}</span>}
          </div>
        </div>
        <button type="button" className="db-drawer-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className="db-drawer-gauge" style={{ borderColor: color }}>
        <svg viewBox="0 0 100 100" className="db-gauge-svg">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="10" />
          <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 40}
            strokeDashoffset={p.level === null ? 2 * Math.PI * 40 : 2 * Math.PI * 40 * (1 - Math.min(1, p.level / 2))}
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
        </svg>
        <div className="db-gauge-center">
          <div className="db-gauge-val" style={{ color }}>{p.level === null ? '—' : p.level.toFixed(1)}</div>
          <div className="db-gauge-label">{levelLabel(p.level)}</div>
        </div>
      </div>

      <div className="db-drawer-metrics">
        <div className="db-dm">
          <Activity size={16} />
          <div>
            <strong>{p.count}</strong>
            <span>pings / 15 min</span>
          </div>
        </div>
        <div className="db-dm">
          <Gauge size={16} />
          <div>
            <strong>{estimatedCrowd !== null ? estimatedCrowd : '—'}</strong>
            <span>pers. estimées</span>
          </div>
        </div>
        <div className="db-dm">
          <TrendingUp size={16} />
          <div>
            <strong>{p.level !== null ? `${Math.round((p.level / 2) * 100)}%` : '—'}</strong>
            <span>occupation</span>
          </div>
        </div>
        <div className="db-dm">
          <Radio size={16} />
          <div>
            <strong>{formatAgo(p.lastTs)}</strong>
            <span>dernier ping</span>
          </div>
        </div>
      </div>

      <div className="db-drawer-section">
        <div className="db-drawer-kicker">Action directe</div>
        {overrideActive ? (
          <div className="db-drawer-override-active">
            <div>
              <strong>Override actif · {crous.adminOverride.status}</strong>
              {crous.adminOverride.message && <span>« {crous.adminOverride.message} »</span>}
            </div>
            <button type="button" className="db-btn db-btn-ghost" disabled={submitting}
              onClick={() => applyOverride({ clear: true })}>
              <Trash2 size={14} /> Lever
            </button>
          </div>
        ) : (
          <div className="db-quick-override">
            {STATUS_OPTIONS.map((o) => (
              <button key={o.value} type="button" disabled={submitting}
                className={`db-btn db-btn-override db-btn-${o.value}`}
                onClick={() => applyOverride({ status: o.value, durationMs: 60 * 60_000 })}>
                {o.label}
              </button>
            ))}
          </div>
        )}
        <span className="db-hint">Override 1 h, visible instantanément par les étudiants.</span>
      </div>

      {feedback && (
        <div className={`db-toast ${feedback.kind === 'ok' ? 'db-toast-ok' : 'db-toast-err'}`}>
          {feedback.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {feedback.msg}
        </div>
      )}

      <Link to={`/etablissement/gestion/ru/${encodeURIComponent(crous.id)}`} className="db-btn db-btn-detail">
        Voir le détail complet <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/* ── Live tab (table) ────────────────────────────────────────────────── */

function normalize(str) {
  return (str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function cityFromId(id) {
  const m = (id || '').match(/^crous\/([^_]+)/);
  return m ? m[1] : '';
}

function LiveTab({ items, pulse, selectedId, setSelectedId, query, setQuery, sortBy, setSortBy }) {
  const [filterCity, setFilterCity] = useState('all');

  const cities = useMemo(() => {
    const map = {};
    for (const c of items) {
      const city = cityFromId(c.id);
      if (!map[city]) map[city] = 0;
      map[city] += 1;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;

    if (filterCity !== 'all') {
      list = list.filter((c) => cityFromId(c.id) === filterCity);
    }

    if (query.trim()) {
      const q = normalize(query);
      list = list.filter((c) => {
        const hay = normalize(
          `${c.name} ${c.campus} ${c.id} ${c.address || ''} ${c.type || ''}`
        );
        return q.split(/\s+/).every((word) => hay.includes(word));
      });
    }

    list = list.map((c) => ({ ...c, _p: pulse[c.id] || { level: null, count: 0, lastTs: null, trend: 'flat' } }));

    list.sort((a, b) => {
      const al = a._p.level;
      const bl = b._p.level;
      switch (sortBy) {
        case 'level-desc':
          if (al === null && bl === null) return 0;
          if (al === null) return 1;
          if (bl === null) return -1;
          return bl - al;
        case 'level-asc':
          if (al === null && bl === null) return 0;
          if (al === null) return 1;
          if (bl === null) return -1;
          return al - bl;
        case 'pings':
          return (b._p.count || 0) - (a._p.count || 0);
        case 'name':
          return (a.name || a.id).localeCompare(b.name || b.id, 'fr');
        default:
          return 0;
      }
    });
    return list;
  }, [items, query, pulse, sortBy, filterCity]);

  const selectedCrous = useMemo(
    () => (selectedId ? items.find((c) => c.id === selectedId) : null),
    [items, selectedId]
  );

  return (
    <>
      <div className="db-toolbar">
        <div className="db-search">
          <Search size={15} />
          <input
            type="search"
            placeholder="Rechercher un RU, campus, ville…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="db-filter-city">
          <div className="db-select-wrap">
            <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}>
              <option value="all">Toutes les villes ({items.length})</option>
              {cities.map(([city, count]) => (
                <option key={city} value={city}>
                  {city.charAt(0).toUpperCase() + city.slice(1)} ({count})
                </option>
              ))}
            </select>
            <ChevronDown size={14} />
          </div>
        </div>
        <div className="db-sort">
          <span>Trier</span>
          <div className="db-select-wrap">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="level-desc">Affluence ↓</option>
              <option value="level-asc">Affluence ↑</option>
              <option value="pings">Pings</option>
              <option value="name">Nom A-Z</option>
            </select>
            <ChevronDown size={14} />
          </div>
        </div>
      </div>

      <div className="db-content">
        <div className="db-table-wrap">
          {filtered.length === 0 ? (
            <div className="db-empty">Aucun RU ne correspond à ta recherche.</div>
          ) : (
            <div className="db-table">
              <div className="db-table-header">
                <span className="db-th db-th-status">Niveau</span>
                <span className="db-th db-th-info">Restaurant</span>
                <span className="db-th db-th-level">Statut</span>
                <span className="db-th db-th-trend">Tend.</span>
                <span className="db-th db-th-pings">Pings</span>
                <span className="db-th db-th-crowd">Est.</span>
                <span className="db-th db-th-last">Dernier</span>
                <span className="db-th db-th-arrow" />
              </div>
              {filtered.map((c) => (
                <RuRow
                  key={c.id}
                  crous={c}
                  pulse={pulse[c.id]}
                  onSelect={setSelectedId}
                  isSelected={selectedId === c.id}
                />
              ))}
            </div>
          )}
        </div>

        {selectedCrous && (
          <DetailDrawer
            crous={selectedCrous}
            pulse={pulse[selectedCrous.id]}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </>
  );
}

/* ── TABS ─────────────────────────────────────────────────────────────── */

const TABS = [
  { key: 'live', label: 'Temps réel', icon: LayoutDashboard },
  { key: 'stats', label: 'Statistiques', icon: BarChart3 },
  { key: 'reviews', label: 'Avis repas', icon: Star }
];

/* ── Main page ───────────────────────────────────────────────────────── */

function DashboardPage() {
  const { items, loading, error } = useManagedCrous();
  const { pulse, loading: pulseLoading } = useAllCrousLive();
  const [activeTab, setActiveTab] = useState('live');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [sortBy, setSortBy] = useState('level-desc');
  const [service, setService] = useState(serviceWindowLabel());

  useEffect(() => {
    const id = setInterval(() => setService(serviceWindowLabel()), 30_000);
    return () => clearInterval(id);
  }, []);

  const stats = useMemo(() => {
    let full = 0, moderate = 0, calm = 0, silent = 0, totalPings = 0;
    for (const c of items) {
      const p = pulse[c.id];
      if (!p || p.level === null) silent += 1;
      else if (p.level >= 1.5) full += 1;
      else if (p.level >= 0.7) moderate += 1;
      else calm += 1;
      if (p) totalPings += p.count;
    }
    return { total: items.length, full, moderate, calm, silent, totalPings };
  }, [items, pulse]);

  const crousIds = useMemo(() => items.map((c) => c.id), [items]);
  const crousMap = useMemo(() => {
    const m = {};
    for (const c of items) m[c.id] = c.name || c.id;
    return m;
  }, [items]);

  const isLoading = loading || pulseLoading;

  return (
    <div className="db-page">
      <div className="db-head">
        <div>
          <span className="db-kicker">Tableau de bord</span>
          <h1>Vue d'ensemble</h1>
        </div>
      </div>

      <KpiBar stats={stats} service={service} />

      {/* Tab navigation */}
      <div className="db-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              className={`db-tab ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {isLoading && <div className="db-empty">Chargement…</div>}
      {error && <div className="db-empty">Impossible de charger les RU.</div>}

      {!isLoading && !error && activeTab === 'live' && (
        <LiveTab
          items={items}
          pulse={pulse}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          query={query}
          setQuery={setQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}

      {!isLoading && !error && activeTab === 'stats' && (
        <StatsTab crousIds={crousIds} crousMap={crousMap} />
      )}

      {!isLoading && !error && activeTab === 'reviews' && (
        <ReviewsTab crousMap={crousMap} />
      )}
    </div>
  );
}

export default DashboardPage;
